import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AllianceMember } from '../types';
import { apiRequest } from '../lib/api';

const Members: React.FC = () => {
  const [members, setMembers] = useState<AllianceMember[]>([]);
  const membersRef = useRef<HTMLDivElement[]>([]);

  // Fetch members from API
  const { data: membersData, isLoading } = useQuery<AllianceMember[]>({
    queryKey: ['alliance-members'],
    queryFn: async () => {
      const apiUrl = (import.meta.env as { VITE_API_URL?: string }).VITE_API_URL || '/api/members';
      const url = `${apiUrl}?alliance=knki`;
      
      try {
        const data = await apiRequest<AllianceMember[]>(url);
        return data || [];
      } catch (error) {
        console.warn('Failed to fetch members, using empty list:', error);
        return [];
      }
    },
    refetchInterval: 120000, // Refetch every 2 minutes
    staleTime: 60000, // Consider data stale after 1 minute
    retry: 2,
  });

  useEffect(() => {
    if (membersData) {
      // Sort members by power (descending) and then by role priority
      const rolePriority: Record<string, number> = {
        Leader: 1,
        'Deputy Leader': 2,
        Officer: 3,
        Member: 4,
      };

      const sorted = [...membersData].sort((a, b) => {
        const roleDiff = (rolePriority[a.role] || 99) - (rolePriority[b.role] || 99);
        if (roleDiff !== 0) return roleDiff;
        return b.power - a.power;
      });

      setMembers(sorted);
    }
  }, [membersData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    membersRef.current.forEach((member: HTMLDivElement | null) => {
      if (member) {
        member.classList.add('fade-in-ready');
        observer.observe(member);
      }
    });

    return () => {
      membersRef.current.forEach((member: HTMLDivElement | null) => {
        if (member) observer.unobserve(member);
      });
    };
  }, [members]);

  const getRoleColor = (role: string): string => {
    switch (role.toLowerCase()) {
      case 'leader':
        return 'text-accent';
      case 'deputy leader':
        return 'text-primary-light';
      case 'officer':
        return 'text-secondary';
      default:
        return 'text-text-secondary';
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatPower = (power: number): string => {
    if (power >= 1000000) {
      return `${(power / 1000000).toFixed(1)}M`;
    }
    if (power >= 1000) {
      return `${(power / 1000).toFixed(1)}K`;
    }
    return power.toLocaleString();
  };

  return (
    <section id="members" className="section">
      <div className="container">
        <h2 className="section-title">Alliance Members</h2>
        
        {isLoading && (
          <div className="text-center py-12">
            <p className="text-text-muted italic">Loading members...</p>
          </div>
        )}

        {!isLoading && members.length === 0 && (
          <div className="text-center py-12">
            <p className="text-text-muted italic">
              Member roster will be displayed here once available.
            </p>
            <p className="text-text-muted text-sm mt-2">
              Connect with us in-game to see our full roster!
            </p>
          </div>
        )}

        {!isLoading && members.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {members.map((member, index) => (
                <div
                  key={member.id || index}
                  className="bg-bg-card p-6 rounded-xl border border-border transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-glow"
                  ref={(el) => {
                    if (el) membersRef.current[index] = el;
                  }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-orbitron text-xl mb-1 text-primary-light">
                        {member.name}
                      </h3>
                      <span className={`text-sm font-semibold uppercase tracking-wide ${getRoleColor(member.role)}`}>
                        {member.role}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary text-sm">Power:</span>
                      <span className="font-orbitron text-lg font-bold text-primary-light">
                        {formatPower(member.power)}
                      </span>
                    </div>
                    {member.joinedDate && (
                      <div className="flex justify-between items-center">
                        <span className="text-text-secondary text-sm">Joined:</span>
                        <span className="text-text-muted text-sm">
                          {formatDate(member.joinedDate)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <p className="text-text-muted text-sm">
                Showing {members.length} {members.length === 1 ? 'member' : 'members'}
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Members;









